import React, { useState, useMemo } from 'react';
import { ResponsiveTreeMap } from '@nivo/treemap';
import { Segmented } from 'antd';
import { AppstoreOutlined, BorderHorizontalOutlined, BorderVerticleOutlined, BlockOutlined, BorderOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { linearGradientDef } from '@nivo/core';
import { rgb, hsl } from 'd3-color';
import { scaleOrdinal } from 'd3-scale';

const getTextColor = (backgroundColor) => {
  const colorObj = rgb(backgroundColor);
  const luminance = 0.299 * colorObj.r + 0.587 * colorObj.g + 0.114 * colorObj.b;
  return luminance > 150 ? 'red' : '#fff';
};

const modernColors = [
  '#54a0ff', '#0077c9', '#055084', '#23455c', '#063054',
];

const NivoTreemapChart = ({
  data,
  identity = "name",
  value = "value",
  nodeOpacity = 0.8,
  initialMode = 'squarify',
  marginX = 10,
  showToolbar = true,
  labelSize = 11
}) => {
  const [tileMode, setTileMode] = useState(initialMode);
  const [currentNode, setCurrentNode] = useState(data);
  const [nodeStack, setNodeStack] = useState([]);

  const tileModes = [
    { label: <AppstoreOutlined />, value: 'slice' },
    { label: <BorderHorizontalOutlined />, value: 'dice' },
    { label: <BorderVerticleOutlined />, value: 'binary' },
    { label: <BlockOutlined />, value: 'sliceDice' },
    { label: <BorderOutlined />, value: 'squarify' },
  ];

  const handleTileModeChange = (value) => {
    setTileMode(value);
  };

  const colorScale = useMemo(() => scaleOrdinal(modernColors), []);

  const generateGradientForNode = (node) => {
    const baseColor = hsl(colorScale(node.id));
    const lightColor = baseColor.brighter(0.5);
    const darkColor = baseColor.darker(0.5);

    return linearGradientDef(
      `gradient-${node.id}`,
      [
        { offset: 0, color: lightColor.toString(), opacity: 0.7 },
        { offset: 100, color: darkColor.toString(), opacity: 1 },
      ],
      {
        gradientTransform: `rotate(${(node.depth % 4) * 90} 0.5 0.5)`,
      }
    );
  };

  const gradientDefs = useMemo(() => {
    const collectNodes = (node, acc = []) => {
      acc.push(generateGradientForNode(node));
      if (node.children) {
        node.children.forEach(child => collectNodes(child, acc));
      }
      return acc;
    };

    return collectNodes(currentNode);
  }, [currentNode, colorScale]);

  const handleNodeClick = (node, event) => {
    if (node.children) {
      setNodeStack([...nodeStack, currentNode]);
      setCurrentNode(node);
    }
  };

  const handleBackClick = () => {
    if (nodeStack.length > 0) {
      const previousNode = nodeStack[nodeStack.length - 1];
      setCurrentNode(previousNode);
      setNodeStack(nodeStack.slice(0, -1));
    }
  };

  return (
    <>
      {showToolbar && (
        <div style={{paddingLeft: '10px', marginBottom: '10px'}}>
          <Segmented options={tileModes} value={tileMode} onChange={handleTileModeChange} />
          {nodeStack.length > 0 && (
            <Segmented
              options={[{ label: <ArrowLeftOutlined />, value: 'back' }]}
              value="back"
              onChange={handleBackClick}
              style={{ marginLeft: '10px' }}
            />
          )}
        </div>
      )}
      <ResponsiveTreeMap
        theme={{
          labels: {
            text: {
              fontSize: labelSize,
              fontWeight: '300',
              fontFamily: "Roboto, sans-serif",
            }
          }
        }}
        data={currentNode}
        tile={tileMode}
        identity={identity}
        value={value}
        borderWidth={0}
        margin={{ top: 10, right: marginX, bottom: 10, left: marginX }}
        labelSkipSize={10}
        label={node => {
          const words = node.id.split(' ');
          const formattedValue = node.formattedValue;

          const wrapText = (text, maxWidth) => {
            const words = text.split(' ');
            const lines = [];
            let currentLine = words[0];

            for (let i = 1; i < words.length; i++) {
              const word = words[i];
              const width = getTextWidth(currentLine + " " + word);

              if (width < maxWidth) {
                currentLine += " " + word;
              } else {
                lines.push(currentLine);
                currentLine = word;
              }
            }
            lines.push(currentLine);
            return lines;
          };

          const getTextWidth = (text) => text.length * 6;

          const wrappedText = wrapText(words.join(' '), node.width);

          return (
            <tspan style={{paddingBottom: '5px'}}>
              {wrappedText.map((line, index) => (
                <tspan
                  key={index}
                  x={-node.width / 2 + 10}
                  dy={index === 0 ? "0em" : '1.2em'}
                  textAnchor="start"
                >
                  {line}
                </tspan>
              ))}
              <tspan
                x={-1}
                dy={'0em'}
                textAnchor="start"
              >
                {formattedValue}
              </tspan>
            </tspan>
          );
        }}
        labelTextColor={node => getTextColor(colorScale(node.id))}
        nodeOpacity={nodeOpacity}
        colors={node => colorScale(node.id)}
        borderColor={{ from: 'color', modifiers: [['darker', 0]] }}
        animate={true}
        motionConfig="gentle"
        defs={gradientDefs}
        fill={[
          { match: '*', id: (node) => `gradient-${node.id}` },
        ]}
        onClick={handleNodeClick}
      />
    </>
  );
};

export default NivoTreemapChart;